import { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Download, Upload, FileSpreadsheet, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Reusable XLSX Import / Export controls.
 *
 * Props:
 *  - exportPath:  '/api/auto-spares/products/export-xlsx'
 *  - importPath:  '/api/auto-spares/products/import-xlsx'
 *  - label:       'Inventory' | 'Parts Catalog'
 *  - testIdPrefix: 'inventory' | 'parts-catalog'
 *  - onImported:  callback after a successful import (refresh list)
 */
export function XlsxImportExport({ exportPath, importPath, label, testIdPrefix, onImported }) {
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [allowSkuChange, setAllowSkuChange] = useState(false);
  const [pickedFile, setPickedFile] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleExport = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}${exportPath}`, {
        headers,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Derive filename from server header or fall back
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : `${label.toLowerCase().replace(/\s+/g, '-')}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${label} exported`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async () => {
    if (!pickedFile) {
      toast.error('Pick a .xlsx file first');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', pickedFile);
      fd.append('allow_sku_change', allowSkuChange ? 'true' : 'false');
      const res = await axios.post(`${API_URL}${importPath}`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success(
        `Import complete — ${res.data.inserted} new, ${res.data.updated} updated${
          res.data.sku_rewritten ? `, ${res.data.sku_rewritten} stock code(s) rewritten` : ''
        }`
      );
      if (onImported) onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const closeAndReset = () => {
    setImportOpen(false);
    setPickedFile(null);
    setResult(null);
    setAllowSkuChange(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Button
        onClick={handleExport}
        disabled={busy}
        variant="outline"
        className="border-emerald-600 text-emerald-300 hover:bg-emerald-600/20"
        data-testid={`${testIdPrefix}-export-xlsx-btn`}
      >
        <Download className="h-4 w-4 mr-2" /> Export XLSX
      </Button>

      <Dialog open={importOpen} onOpenChange={(o) => { if (!o) closeAndReset(); else setImportOpen(true); }}>
        <DialogTrigger asChild>
          <Button
            variant="outline"
            className="border-amber-600 text-amber-300 hover:bg-amber-600/20"
            data-testid={`${testIdPrefix}-import-xlsx-btn`}
          >
            <Upload className="h-4 w-4 mr-2" /> Import XLSX
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5 text-amber-400" /> Import {label} from .xlsx
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Upload an .xlsx workbook to bulk add or update {label.toLowerCase()} entries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-900/50 border border-slate-700 rounded p-3 text-xs text-slate-300 space-y-1">
              <p>
                <span className="font-bold text-white">Tip:</span> click <strong>Export XLSX</strong> first to get a
                template with the right column headers and existing rows.
              </p>
              <p>
                Rows are matched by <strong>ID</strong> first, then by <strong>Stock Code</strong>. Missing matches
                are inserted as new entries.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">Workbook (.xlsx)</label>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setPickedFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-200 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-amber-600 file:text-white hover:file:bg-amber-700 file:cursor-pointer"
                data-testid={`${testIdPrefix}-import-file-input`}
              />
            </div>

            <label className="flex items-start gap-3 cursor-pointer bg-slate-900/50 border border-slate-700 rounded p-3 hover:bg-slate-900/70">
              <input
                type="checkbox"
                checked={allowSkuChange}
                onChange={(e) => setAllowSkuChange(e.target.checked)}
                className="mt-1 h-4 w-4 accent-rose-500"
                data-testid={`${testIdPrefix}-allow-sku-change-toggle`}
              />
              <div>
                <p className="text-sm font-semibold text-white flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-rose-400" />
                  Allow Stock Code Rewrite
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Off (default): only name / category / prices / qty are updated; existing Stock Codes are preserved.
                  On: any new Stock Code in the file will overwrite the existing one on that row.
                </p>
              </div>
            </label>

            <Button
              onClick={handleImport}
              disabled={busy || !pickedFile}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid={`${testIdPrefix}-import-submit-btn`}
            >
              <Upload className="h-4 w-4 mr-2" />
              {busy ? 'Importing…' : 'Run Import'}
            </Button>

            {result && (
              <div className="bg-slate-900/60 border border-slate-700 rounded p-3 space-y-1" data-testid={`${testIdPrefix}-import-result`}>
                <p className="text-sm flex items-center gap-2 text-emerald-300">
                  <CheckCircle2 className="h-4 w-4" /> {result.message}
                </p>
                <ul className="text-xs text-slate-300 ml-6 list-disc">
                  <li>Inserted: <span className="text-emerald-400 font-semibold">{result.inserted}</span></li>
                  <li>Updated: <span className="text-blue-400 font-semibold">{result.updated}</span></li>
                  <li>Stock codes rewritten: <span className="text-amber-400 font-semibold">{result.sku_rewritten}</span></li>
                  <li>Skipped: <span className="text-rose-400 font-semibold">{result.skipped}</span></li>
                </ul>
                {result.errors?.length > 0 && (
                  <details className="text-xs text-rose-300 mt-2">
                    <summary className="cursor-pointer">{result.errors.length} skipped row{result.errors.length === 1 ? '' : 's'}</summary>
                    <ul className="ml-4 mt-1 list-disc">
                      {result.errors.map((er, i) => (
                        <li key={i}>Row {er.row}: {er.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
